import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, Compass, Sparkles, MessageCircleHeart, CheckCircle2, Target, Users, ClipboardCheck, Scale, Sun, Moon, Keyboard, Eye, EyeOff, Loader2, Menu, X, Settings, ChevronDown, HardHat } from 'lucide-react';
import { piePersona, pieApi } from './pieApi';
import { getInitialTheme, setStoredTheme, applyThemeToShells } from './pieTheme';
import AcmeLogo from './AcmeLogo';
import PIEPersonaChip from './PIEPersonaChip';
import { useAuth } from '../../context/AuthContext';
import logger from '../../lib/logger';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import useFavicon from '../../hooks/useFavicon';
import useMirrorAppIcons from '../../hooks/useMirrorAppIcons';
import useMirrorPresence from '../../hooks/useMirrorPresence';
import { PIEDisplayModeProvider, usePIEDisplayMode } from './PIEDisplayMode';

const MIRROR_PAGE_TITLES = {
  '/mirror': 'Today',
  '/mirror/dashboard': 'Today',
  '/mirror/improve': 'What to improve',
  '/mirror/simulate': 'What if',
  '/mirror/validate': 'Did it work?',
  '/mirror/coach': 'Coach',
  '/mirror/frontline': 'Frontline demo',
  '/mirror/calibration': 'Calibration',
  '/mirror/team': 'Team',
  '/mirror/manager-pulse': 'Manager pulse',
  '/mirror/pulses': 'Pulse history',
};

/**
 * PIELayout — modern, board-ready chrome wrapping every PIE page.
 *
 * Aligned to the Seertech ROI Calculator visual language: white
 * background, dark navy + teal accent, Inter typography, sticky
 * top nav with the (currently hard-coded) Acme org logo. When PIE
 * graduates from prototype the AcmeLogo component will be swapped
 * for `<OrgLogo src={org.logo_url} />` so each tenant renders its
 * own brand here.
 */
export default function PIELayout({ children, narrow = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [showTeamNav, setShowTeamNav] = useState(false);
  const [theme, setTheme] = useState(() => getInitialTheme());
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [aiHealth, setAiHealth] = useState(null);
  // Mobile drawer + per-cluster popover state. Each is independent so
  // opening Manage doesn't accidentally close the display-mode pop.
  const [mobileOpen, setMobileOpen] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);
  const { isSuperAdmin } = useAuth();

  // Whenever the user navigates, slam every popover/drawer shut so
  // they're never carried into the next route.
  useEffect(() => {
    setMobileOpen(false);
    setManageOpen(false);
  }, [location.pathname]);

  // Lock body scroll when the mobile drawer is open so the page
  // beneath doesn't bleed through. Restored on close.
  useEffect(() => {
    if (mobileOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = prev; };
    }
  }, [mobileOpen]);

  // Browser tab title — wins over the Mission Control header title for
  // every /mirror/* route. Falls back gracefully on unknown sub-routes.
  const mirrorPageTitle = useMemo(() => {
    if (MIRROR_PAGE_TITLES[location.pathname]) return MIRROR_PAGE_TITLES[location.pathname];
    // Strip query/hash, try the base path without trailing segments.
    const segs = location.pathname.split('/').filter(Boolean);
    while (segs.length > 1) {
      segs.pop();
      const candidate = '/' + segs.join('/');
      if (MIRROR_PAGE_TITLES[candidate]) return MIRROR_PAGE_TITLES[candidate];
    }
    return null;
  }, [location.pathname]);
  useDocumentTitle(
    mirrorPageTitle ? `Mirror · ${mirrorPageTitle} · Seertech` : 'Mirror · Seertech',
  );
  // Distinct favicon for Mirror tabs so users with both products open can
  // tell them apart at a glance. Auto-restores to the Seertech mark on unmount.
  useFavicon('/mirror_favicon.png?v=2');
  // Give Mirror its own home-screen / PWA identity (Seertech monogram icon +
  // "Mirror" app name) while on /mirror/*, restored on exit.
  useMirrorAppIcons(true);
  // Cross-tab presence: Mission Control sidebar reads this heartbeat to
  // surface an ambient "You're also in Mirror" pill so super admins
  // juggling both surfaces never lose context.
  useMirrorPresence({ heartbeat: true });

  // Apply the theme on mount and any time it changes. Scoped to
  // .pie-shell elements so the LIE app's theme is untouched.
  useEffect(() => {
    applyThemeToShells(theme);
  }, [theme]);

  const onToggleTheme = () => {
    const next = theme === 'dark' ? 'light' : 'dark';
    setTheme(next);
    setStoredTheme(next);
  };

  const onExit = () => {
    piePersona.clear();
    // Opened from Mission Control in its own tab → hand focus back to that tab and close this one.
    const opener = window.opener;
    if (opener && !opener.closed) {
      try { opener.focus(); window.close(); return; } catch { /* fall through */ }
    }
    navigate('/');
  };

  // ── Keyboard shortcuts ────────────────────────────────────────
  // `g` is the leader key. After pressing `g`, a single subsequent
  // letter routes you to a PIE page (gd → today, gi → improve, gw
  // → what-if, gv → validate, gc → coach, gt → team). `?` opens
  // the help modal. `Esc` closes the help modal. Any key inside an
  // editable element is ignored so we don't fight the user's typing.
  useEffect(() => {
    let pendingG = false;
    let timer = null;
    const isEditable = (el) => {
      if (!el) return false;
      const tag = el.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
      if (el.isContentEditable) return true;
      return false;
    };
    const onKey = (e) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (isEditable(document.activeElement)) return;
      if (e.key === '?' && e.shiftKey) {
        e.preventDefault();
        setShortcutsOpen(true);
        return;
      }
      if (e.key === 'Escape' && shortcutsOpen) {
        e.preventDefault();
        setShortcutsOpen(false);
        return;
      }
      if (!pendingG && e.key === 'g') {
        pendingG = true;
        timer = setTimeout(() => { pendingG = false; }, 1000);
        return;
      }
      if (pendingG) {
        pendingG = false;
        if (timer) { clearTimeout(timer); timer = null; }
        const map = {
          d: '/mirror/dashboard',
          i: '/mirror/improve',
          w: '/mirror/simulate',
          v: '/mirror/validate',
          c: '/mirror/coach',
          t: '/mirror/team',
          m: '/mirror/manager-pulse',
          h: '/',         // Mission Control home — matches the MC global `g h`.
          '.': '/mirror', // Mirror landing (persona picker).
        };
        const target = map[e.key.toLowerCase()];
        if (target) {
          e.preventDefault();
          navigate(target);
        }
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      if (timer) clearTimeout(timer);
    };
  }, [navigate, shortcutsOpen]);

  // Fetch the team snapshot on mount — if the active persona has any
  // reports we surface the "My team" nav link. Failures are silent;
  // the link just stays hidden.
  useEffect(() => {
    let mounted = true;
    pieApi
      .teamSnapshot()
      .then((d) => { if (mounted) setShowTeamNav(!!(d?.is_manager || d?.team_size)); })
      .catch((e) => { logger.warn('team snapshot probe failed', e); });
    return () => { mounted = false; };
  }, []);

  // Silent AI health probe — drives the small status pill. Failures
  // are swallowed so a broken endpoint never hides the chrome.
  useEffect(() => {
    let mounted = true;
    pieApi
      .health()
      .then((d) => { if (mounted) setAiHealth(d); })
      .catch(() => { /* swallow — pill stays hidden */ });
    return () => { mounted = false; };
  }, []);

  const linkClass = ({ isActive }) =>
    `pie-nav-link ${isActive ? 'pie-nav-link-active' : ''}`;

  return (
    <PIEDisplayModeProvider>
    <div className={`pie-shell ${theme === 'dark' ? 'pie-dark' : ''}`} data-testid="pie-shell">
      <header className="pie-header" data-testid="pie-header">
        <div className="pie-header-inner">
          {/* Brand — slim. Subtitle "See it together." removed; the
              hero on /mirror already carries the tagline. */}
          <Link to="/mirror" className="pie-org-brand" data-testid="pie-brand">
            <AcmeLogo height={28} />
            <span className="pie-org-divider" />
            <span className="pie-product-name">
              <span className="pie-product-name-brand">Seertech</span> Mirror
            </span>
          </Link>

          {/* Desktop primary nav — 5 consumer items + a "Manage"
              dropdown for the manager/admin items so the bar never
              swells past 5 visible pills. */}
          <nav className="pie-nav pie-nav-desktop" aria-label="Seertech Mirror navigation">
            <NavLink to="/mirror/dashboard" className={linkClass} data-testid="pie-nav-dashboard">
              <Compass size={14} aria-hidden /> Today
            </NavLink>
            <NavLink to="/mirror/improve" className={linkClass} data-testid="pie-nav-improve">
              <Target size={14} aria-hidden /> What to improve
            </NavLink>
            <NavLink to="/mirror/simulate" className={linkClass} data-testid="pie-nav-simulate">
              <Sparkles size={14} aria-hidden /> What if
            </NavLink>
            <NavLink to="/mirror/validate" className={linkClass} data-testid="pie-nav-validate">
              <CheckCircle2 size={14} aria-hidden /> Did it work?
            </NavLink>
            <NavLink to="/mirror/coach" className={linkClass} data-testid="pie-nav-coach">
              <MessageCircleHeart size={14} aria-hidden /> Coach
            </NavLink>
            <NavLink to="/mirror/frontline" className={linkClass} data-testid="pie-nav-frontline">
              <HardHat size={14} aria-hidden /> Frontline demo</NavLink>
            {(showTeamNav || isSuperAdmin) && (
              <ManageDropdown
                open={manageOpen}
                onToggle={() => setManageOpen((v) => !v)}
                onClose={() => setManageOpen(false)}
                showTeam={showTeamNav}
                showCalibration={isSuperAdmin}
                activePath={location.pathname}
              />
            )}
          </nav>

          {/* Right-side action cluster. Icon-first so it survives the
              squeeze on narrow viewports. */}
          <div className="pie-header-actions" data-testid="pie-header-actions">
            {aiHealth && <PIEAIStatusPill health={aiHealth} />}
            {isSuperAdmin && <CompactDisplayModeButton />}
            <button
              type="button"
              onClick={onToggleTheme}
              className="pie-icon-btn"
              data-testid="pie-theme-toggle"
              aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
              title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {theme === 'dark' ? <Sun size={14} aria-hidden /> : <Moon size={14} aria-hidden />}
            </button>
            <button
              onClick={onExit}
              className="pie-exit pie-exit-responsive"
              data-testid="pie-exit-btn"
              type="button"
              title="Back to Mission Control"
              aria-label="Back to Mission Control"
            >
              <ArrowLeft size={14} aria-hidden />
              <span className="pie-exit-label">Mission Control</span>
            </button>
            {/* Hamburger — only visible on small screens (<900px). */}
            <button
              type="button"
              className="pie-mobile-menu-btn"
              onClick={() => setMobileOpen(true)}
              aria-label="Open navigation menu"
              aria-expanded={mobileOpen}
              data-testid="pie-mobile-menu-btn"
            >
              <Menu size={18} aria-hidden />
            </button>
          </div>
        </div>
      </header>
      {mobileOpen && (
        <MobileDrawer
          onClose={() => setMobileOpen(false)}
          showTeam={showTeamNav}
          showCalibration={isSuperAdmin}
          onExit={onExit}
          theme={theme}
          onToggleTheme={onToggleTheme}
          isSuperAdmin={isSuperAdmin}
        />
      )}
      <PIEPersonaChip />
      <main className="pie-main">
        <div className={narrow ? 'pie-container-narrow' : 'pie-container'}>{children}</div>
      </main>
      <footer className="pie-footer">
        Seertech Mirror · prototype · synthetic data
      </footer>
      {shortcutsOpen && (
        <ShortcutsModal onClose={() => setShortcutsOpen(false)} />
      )}
    </div>
    </PIEDisplayModeProvider>
  );
}

/**
 * ShortcutsModal — a tiny help overlay listing every keyboard
 * shortcut available inside PIE. Triggered by `?` or the keyboard
 * icon in the header. ESC dismisses.
 */
function ShortcutsModal({ onClose }) {
  const groups = [
    {
      title: 'Navigation (press g, then…)',
      rows: [
        ['g · d', 'Today'],
        ['g · i', 'What to improve'],
        ['g · w', 'What if'],
        ['g · v', 'Did it work?'],
        ['g · c', 'Coach'],
        ['g · t', 'My team'],
        ['g · m', 'Manager pulse'],
        ['g · h', 'Mission Control home'],
        ['g · .', 'Mirror landing (persona picker)'],
      ],
    },
    {
      title: 'General',
      rows: [
        ['?', 'Open this help'],
        ['Esc', 'Close popovers / modals'],
      ],
    },
  ];
  return (
    <div
      className="pie-shortcuts-overlay"
      onClick={onClose}
      data-testid="pie-shortcuts-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Keyboard shortcuts"
    >
      <div
        className="pie-shortcuts-modal"
        onClick={(e) => e.stopPropagation()}
        data-testid="pie-shortcuts-modal"
      >
        <div className="pie-shortcuts-head">
          <Keyboard size={16} aria-hidden style={{ color: 'var(--pie-accent)' }} />
          <h2>Keyboard shortcuts</h2>
        </div>
        <p className="pie-shortcuts-tip">
          PIE-only. Type fast — <span className="pie-mono">g</span> is the leader key.
        </p>
        {groups.map((g) => (
          <section key={g.title} className="pie-shortcuts-group">
            <div className="pie-shortcuts-group-title">{g.title}</div>
            <ul className="pie-shortcuts-list">
              {g.rows.map(([combo, label]) => (
                <li key={combo}>
                  <kbd className="pie-mono">{combo}</kbd>
                  <span>{label}</span>
                </li>
              ))}
            </ul>
          </section>
        ))}
        <button
          type="button"
          className="pie-btn pie-btn-ghost pie-shortcuts-close"
          onClick={onClose}
          data-testid="pie-shortcuts-close"
        >
          Close (Esc)
        </button>
      </div>
    </div>
  );
}


/**
 * PIEAIStatusPill — sits next to the theme toggle in the PIE chrome.
 * Shows a coloured dot + AI status string. Tooltip carries the full
 * user-friendly explainer pulled from /api/personal/health so users
 * understand WHY the Coach might feel canned (deterministic fallback).
 *
 * Hidden when status is green to avoid visual clutter — the dot only
 * appears when the user is actually in degraded mode and needs to
 * know why their AI experience differs from the marketing.
 */
function PIEAIStatusPill({ health }) {
  if (!health || health.ai_status === 'green') return null;

  const tone = health.ai_status === 'red'
    ? { dot: '#DC2626', bg: 'rgba(220, 38, 38, 0.08)', label: 'AI offline' }
    : { dot: '#F59E0B', bg: 'rgba(245, 158, 11, 0.10)', label: 'AI slow' };

  return (
    <span
      data-testid="pie-ai-status-pill"
      title={health.user_message}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        padding: '4px 10px', borderRadius: 999,
        background: tone.bg, color: tone.dot,
        fontSize: 11, fontWeight: 600, letterSpacing: 0.2,
        border: `1px solid ${tone.dot}33`,
      }}
    >
      <span
        aria-hidden
        style={{
          width: 7, height: 7, borderRadius: '50%',
          background: tone.dot,
          animation: health.ai_status === 'red' ? 'pie-pulse 1.5s ease-in-out infinite' : 'none',
        }}
      />
      {tone.label}
    </span>
  );
}

/**
 * ManageDropdown — collapses My team / Manager pulse / Calibration
 * into a single "Manage" pill so the primary nav stays at 5 visible
 * items even for super-admins. Active when the current path matches
 * any of its children. Click-outside + ESC + route-change all close it.
 */
function ManageDropdown({ open, onToggle, onClose, showTeam, showCalibration, activePath }) {
  const ref = useRef(null);
  const childPaths = ['/mirror/team', '/mirror/manager-pulse', '/mirror/calibration'];
  const isActive = childPaths.some((p) => activePath.startsWith(p));

  // Close on outside click + ESC. We don't fight the Link clicks
  // inside our own subtree.
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  return (
    <div className="pie-nav-dropdown" ref={ref} data-testid="pie-nav-manage">
      <button
        type="button"
        className={`pie-nav-link pie-nav-dropdown-toggle ${isActive ? 'pie-nav-link-active' : ''}`}
        onClick={onToggle}
        aria-expanded={open}
        aria-haspopup="menu"
        data-testid="pie-nav-manage-toggle"
      >
        <Settings size={14} aria-hidden /> Manage
        <ChevronDown
          size={12}
          aria-hidden
          className={`pie-nav-dropdown-chev ${open ? 'pie-nav-dropdown-chev-open' : ''}`}
        />
      </button>
      {open && (
        <div className="pie-nav-dropdown-menu" role="menu" data-testid="pie-nav-manage-menu">
          {showTeam && (
            <NavLink
              to="/mirror/team"
              className={({ isActive: a }) => `pie-nav-dropdown-item ${a ? 'pie-nav-dropdown-item-active' : ''}`}
              role="menuitem"
              data-testid="pie-nav-team"
            >
              <Users size={14} aria-hidden /> My team
            </NavLink>
          )}
          {showTeam && (
            <NavLink
              to="/mirror/manager-pulse"
              className={({ isActive: a }) => `pie-nav-dropdown-item ${a ? 'pie-nav-dropdown-item-active' : ''}`}
              role="menuitem"
              data-testid="pie-nav-manager-pulse"
            >
              <ClipboardCheck size={14} aria-hidden /> Manager pulse
            </NavLink>
          )}
          {showCalibration && (
            <NavLink
              to="/mirror/calibration"
              className={({ isActive: a }) => `pie-nav-dropdown-item ${a ? 'pie-nav-dropdown-item-active' : ''}`}
              role="menuitem"
              data-testid="pie-nav-calibration"
            >
              <Scale size={14} aria-hidden /> Calibration
            </NavLink>
          )}
        </div>
      )}
    </div>
  );
}


/**
 * MobileDrawer — full-screen slide-in nav for viewports <900px.
 * Surfaces every nav link including the manager/admin section
 * (no need to nest the dropdown on mobile — vertical space is cheap).
 * Closes on link click (handled by parent's route-change effect),
 * the X button, or backdrop click.
 */
function MobileDrawer({ onClose, showTeam, showCalibration, onExit, theme, onToggleTheme, isSuperAdmin }) {
  // ESC dismisses, matching the desktop popover convention.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const itemClass = ({ isActive }) =>
    `pie-mobile-nav-item ${isActive ? 'pie-mobile-nav-item-active' : ''}`;

  return (
    <div
      className="pie-mobile-drawer-overlay"
      onClick={onClose}
      data-testid="pie-mobile-drawer-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Mirror navigation"
    >
      <aside
        className="pie-mobile-drawer"
        onClick={(e) => e.stopPropagation()}
        data-testid="pie-mobile-drawer"
      >
        <div className="pie-mobile-drawer-head">
          <span className="pie-product-name">
            <span className="pie-product-name-brand">Seertech</span> Mirror
          </span>
          <button
            type="button"
            onClick={onClose}
            className="pie-icon-btn"
            aria-label="Close navigation"
            data-testid="pie-mobile-drawer-close"
          >
            <X size={16} aria-hidden />
          </button>
        </div>

        <nav className="pie-mobile-nav" aria-label="Seertech Mirror navigation">
          <NavLink to="/mirror/dashboard" className={itemClass}>
            <Compass size={15} aria-hidden /> Today
          </NavLink>
          <NavLink to="/mirror/improve" className={itemClass}>
            <Target size={15} aria-hidden /> What to improve
          </NavLink>
          <NavLink to="/mirror/simulate" className={itemClass}>
            <Sparkles size={15} aria-hidden /> What if
          </NavLink>
          <NavLink to="/mirror/validate" className={itemClass}>
            <CheckCircle2 size={15} aria-hidden /> Did it work?
          </NavLink>
          <NavLink to="/mirror/coach" className={itemClass}>
            <MessageCircleHeart size={15} aria-hidden /> Coach
          </NavLink>
          <NavLink to="/mirror/frontline" className={itemClass} data-testid="pie-mobile-nav-frontline">
            <HardHat size={15} aria-hidden /> Frontline demo
          </NavLink>

          {(showTeam || showCalibration) && (
            <>
              <div className="pie-mobile-nav-divider" aria-hidden />
              <div className="pie-mobile-nav-section">Manage</div>
              {showTeam && (
                <NavLink to="/mirror/team" className={itemClass}>
                  <Users size={15} aria-hidden /> My team
                </NavLink>
              )}
              {showTeam && (
                <NavLink to="/mirror/manager-pulse" className={itemClass}>
                  <ClipboardCheck size={15} aria-hidden /> Manager pulse
                </NavLink>
              )}
              {showCalibration && (
                <NavLink to="/mirror/calibration" className={itemClass}>
                  <Scale size={15} aria-hidden /> Calibration
                </NavLink>
              )}
            </>
          )}
        </nav>

        <div className="pie-mobile-drawer-foot">
          {isSuperAdmin && <CompactDisplayModeButton placement="mobile" />}
          <button
            type="button"
            onClick={onToggleTheme}
            className="pie-mobile-foot-btn"
            data-testid="pie-mobile-theme-toggle"
          >
            {theme === 'dark' ? <Sun size={14} aria-hidden /> : <Moon size={14} aria-hidden />}
            <span>{theme === 'dark' ? 'Light mode' : 'Dark mode'}</span>
          </button>
          <button
            type="button"
            onClick={onExit}
            className="pie-mobile-foot-btn"
            data-testid="pie-mobile-exit-btn"
          >
            <ArrowLeft size={14} aria-hidden />
            <span>Mission Control</span>
          </button>
        </div>
      </aside>
    </div>
  );
}


/**
 * CompactDisplayModeButton — replaces the long org-name pill that
 * was overflowing the header. Renders an Eye/EyeOff icon button;
 * click opens a popover containing the org name, last-changed
 * stamp, and the actual numbers↔verbiage toggle. Hidden when no
 * org context is resolved.
 *
 * placement="mobile" inlines the popover content directly (no
 * floating menu) so the drawer footer can use it as a normal row.
 */
function CompactDisplayModeButton({ placement = 'desktop' }) {
  const { mode, orgId, orgName, updatedAt, loaded, setMode } = usePIEDisplayMode();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const popRef = useRef(null);

  useEffect(() => {
    if (!open || placement === 'mobile') return undefined;
    const onDoc = (e) => {
      if (popRef.current && !popRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, placement]);

  if (!loaded || !orgId) return null;

  const verbiage = mode === 'verbiage';
  const nextLabel = verbiage ? 'Show numbers' : 'Show verbiage';
  const stampLabel = updatedAt ? _relativeStamp(updatedAt) : 'default';
  const onToggle = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await setMode(verbiage ? 'numbers' : 'verbiage');
    } finally {
      setBusy(false);
    }
  };

  // ── Mobile placement: render inline content (no popover wrapper).
  if (placement === 'mobile') {
    return (
      <div className="pie-display-mobile-row" data-testid="pie-superadmin-display-toggle-group">
        <span className="pie-display-mobile-label">
          <span
            aria-hidden
            className="pie-display-dot"
            style={{ background: verbiage ? '#14b8a6' : '#94a3b8' }}
          />
          <span className="pie-display-mobile-name">{orgName || orgId}</span>
          <span className="pie-display-mobile-stamp">· {stampLabel}</span>
        </span>
        <button
          type="button"
          onClick={onToggle}
          disabled={busy}
          className="pie-mobile-foot-btn"
          data-testid="pie-superadmin-display-toggle"
          data-mode={mode}
        >
          {busy ? <Loader2 size={12} className="animate-spin" aria-hidden />
            : verbiage ? <EyeOff size={12} aria-hidden />
            : <Eye size={12} aria-hidden />}
          <span>{nextLabel}</span>
        </button>
      </div>
    );
  }

  // ── Desktop placement: icon button + popover.
  return (
    <div
      className="pie-display-pop"
      ref={popRef}
      data-testid="pie-superadmin-display-toggle-group"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`pie-icon-btn ${open ? 'pie-icon-btn-on' : ''}`}
        aria-expanded={open}
        aria-haspopup="dialog"
        data-testid="pie-superadmin-display-pop-trigger"
        title={`${orgName || orgId} · display mode (super admin)`}
      >
        {verbiage ? <EyeOff size={14} aria-hidden /> : <Eye size={14} aria-hidden />}
      </button>
      {open && (
        <div className="pie-display-pop-menu" role="dialog" data-testid="pie-superadmin-display-pop-menu">
          <div className="pie-display-pop-eyebrow">Super admin · display mode</div>
          <div className="pie-display-pop-org" data-testid="pie-superadmin-display-toggle-org">
            <span
              aria-hidden
              className="pie-display-dot"
              style={{ background: verbiage ? '#14b8a6' : '#94a3b8' }}
            />
            <span className="pie-display-pop-name">{orgName || orgId}</span>
          </div>
          <div className="pie-display-pop-stamp">Saved {stampLabel}</div>
          <button
            type="button"
            onClick={onToggle}
            disabled={busy}
            className="pie-display-pop-cta"
            data-testid="pie-superadmin-display-toggle"
            data-mode={mode}
          >
            {busy ? <Loader2 size={13} className="animate-spin" aria-hidden />
              : verbiage ? <EyeOff size={13} aria-hidden />
              : <Eye size={13} aria-hidden />}
            <span>{nextLabel}</span>
          </button>
          <p className="pie-display-pop-foot">
            Affects all users in this org.
          </p>
        </div>
      )}
    </div>
  );
}

/**
 * Human-readable "x min ago" / "yesterday" stamp for the toggle's
 * org indicator. Keeps the chip compact while still answering "did
 * I just flip this org, or was that days ago?".
 */
function _relativeStamp(iso) {
  try {
    const t = new Date(iso).getTime();
    if (Number.isNaN(t)) return 'default';
    const diffMs = Date.now() - t;
    const min = Math.round(diffMs / 60000);
    if (min < 1) return 'just now';
    if (min < 60) return `${min}m ago`;
    const hr = Math.round(min / 60);
    if (hr < 24) return `${hr}h ago`;
    const d = Math.round(hr / 24);
    if (d < 7) return `${d}d ago`;
    return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch (e) {
    return 'default';
  }
}
